import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ArrowDownIcon,
  Box,
  Card,
  CheckIcon,
  CloseIcon,
  FilterIcon,
  GKButton,
  GKCheckbox,
  GKModal,
  GKTextField,
  Grid2,
  InfoItalicIcon,
  SearchIcon,
  Typography,
} from "components";
import { useEffect, useState } from "react";
import { PortfolioDropdownInterface } from "utils/types";
import SelectFamilyModal from "./SelectFamilyModal";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  portfolioList?: any[];
  isPortfolioLoading?: boolean;
  selectedItems?: any[];
  selectedAssetTypes?: string[];
  onApply: (selectedPortfolios: any[], assetTypes: string[]) => void;
}

const FilterModal = (props: Props) => {
  const {
    open,
    setOpen,
    portfolioList,
    onApply,
    selectedItems: propSelectedItems,
    selectedAssetTypes: propSelectedAssetTypes,
  } = props;

  const [selectedItems, setSelectedItems] = useState<
    PortfolioDropdownInterface[]
  >(propSelectedItems || []);

  useEffect(() => {
    if (propSelectedItems) {
      setSelectedItems(propSelectedItems);
    }
  }, [propSelectedItems, open]);

  const [selected, setSelected] = useState("All");

  useEffect(() => {
    if (propSelectedAssetTypes) {
      if (propSelectedAssetTypes.length === 0) {
        setSelected("All");
      } else if (propSelectedAssetTypes[0] === "MF") {
        setSelected("Mutual Funds");
      } else if (propSelectedAssetTypes[0] === "EQ") {
        setSelected("Equity");
      }
    }
  }, [propSelectedAssetTypes, open]);

  const [searchText, setSearchText] = useState("");

  const [openFamilyModal, setIsOpenFamilyModal] = useState(false);

  const [selectedFamilyName, setSelectedFamilyName] = useState<string | null>(
    null,
  );

  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);

  const filteredResults = portfolioList?.filter((item) => {
    const search = searchText.toLowerCase();

    const matchClient =
      item.name?.toLowerCase().includes(search) ||
      item.code?.toLowerCase().includes(search);

    const matchPortfolio = item.portfolios?.some((p: any) => {
      return (
        p.name?.toLowerCase().includes(search) ||
        String(p.portfolioId).toLowerCase().includes(search)
      );
    });

    return matchClient || matchPortfolio;
  });

  const clients =
    filteredResults?.filter((item) => item.type === "client") ?? [];

  const familyClients = !selectedFamilyId
    ? clients
    : clients.filter((c) =>
        c.family?.some((f: any) => f.id === selectedFamilyId),
      );

  const finalClientList = familyClients.filter((item) => {
    const s = searchText.toLowerCase();

    return (
      item.name?.toLowerCase().includes(s) ||
      item.code?.toLowerCase().includes(s) ||
      item.portfolios?.some((p: any) => p.name?.toLowerCase().includes(s))
    );
  });

  const typeMap: any = {
    All: [],
    "Mutual Funds": ["MF"],
    Equity: ["EQ"],
  };

  const options = ["All", "Mutual Funds", "Equity"];

  return (
    <>
      <GKModal
        width={"600px"}
        maxHeight={"90%"}
        open={open}
        setOpen={setOpen}
        modalTitle="Apply Filter"
        footer={
          <GKButton
            variant="contained"
            sx={{ width: "120px" }}
            startIcon={<CheckIcon />}
            disabled={selectedItems.length === 0}
            onClick={() => {
              const instType = typeMap[selected] || [""];
              onApply(selectedItems, instType);
              setOpen(false);
            }}
          >
            Apply
          </GKButton>
        }
      >
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <Typography fontSize={13} fontWeight={400} mb={1}>
              Asset Type
            </Typography>

            {options.map((item) => {
              const isSelected = selected === item;

              return (
                <GKButton
                  key={item}
                  onClick={() => setSelected(item)}
                  variant="outlined"
                  startIcon={isSelected ? <CheckIcon /> : null}
                  sx={{
                    borderColor: "#D5DEE5",
                    backgroundColor: isSelected ? "#E6F2F7" : "transparent",
                    color: isSelected ? "#006288" : "#000",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    borderRadius: "10px",
                    marginRight: "8px",
                  }}
                >
                  {item}
                </GKButton>
              );
            })}
          </Grid2>

          <Grid2 size={12} mt={0.5}>
            <Grid2
              size={12}
              mb={1}
              mt={3}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ minHeight: 30 }}
            >
              <Typography fontSize={13} fontWeight={400}>
                Select Portfolios & Clients*
              </Typography>

              {selectedFamilyName ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--grey-500)"
                  >
                    Applied Filter:
                  </Typography>

                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                      background: "var(--grey-50)",
                      borderRadius: "20px",
                      padding: "6px 8px",
                      cursor: "pointer",
                    }}
                  >
                    <Typography fontSize={13} fontWeight={400} color="#0B0D12">
                      {selectedFamilyName}
                    </Typography>

                    <CloseIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFamilyId(null);
                        setSelectedFamilyName(null);
                        setSelectedItems([]);
                        setSearchText("");
                      }}
                      style={{ fontSize: 14 }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  sx={{ cursor: "pointer" }}
                  onClick={() => setIsOpenFamilyModal(true)}
                >
                  <FilterIcon
                    style={{ marginRight: 4, fontSize: 16 }}
                    color="var(--primary-900)"
                  />
                  <Typography
                    fontSize={13}
                    fontWeight={500}
                    color="var(--primary-900)"
                  >
                    Select Family
                  </Typography>
                </Box>
              )}
            </Grid2>

            <GKTextField
              slotProps={{
                input: {
                  endAdornment: (
                    <SearchIcon width={20} color={"var(--grey-500)"} />
                  ),
                  placeholder: "Select Portfolios & Clients",
                  onChange: (e) => setSearchText(e.target.value),
                },
              }}
            />
          </Grid2>

          <Grid2
            size={12}
            mt={1}
            mb={1}
            display="checkflex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography fontSize={12} fontWeight={400} color="var(--grey-500)">
              Search Results
            </Typography>
          </Grid2>

          <Grid2 size={12}>
            {selectedFamilyId && familyClients.length === 0 ? (
              <Card
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "260px",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  fontSize={14}
                  fontWeight={700}
                  color="var(--black-900)"
                  mb={0.5}
                >
                  This family has no members
                </Typography>

                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--grey-500)"
                >
                  Add one or more clients to start viewing family-level
                  insights.
                </Typography>
              </Card>
            ) : finalClientList?.length === 0 ? (
              <Card
                sx={{
                  display: "flex",
                  height: "260px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--grey-500)"
                >
                  No results found
                </Typography>
              </Card>
            ) : (
              <Card sx={{ height: "260px", p: 0, overflowY: "auto" }}>
                {finalClientList?.map((client) => (
                  <Accordion
                    key={client.id}
                    disableGutters
                    sx={{
                      backgroundColor: client.portfolios?.some((p: any) =>
                        selectedItems.includes(p.id || p.portfolioId),
                      )
                        ? "var(--neutral-50)"
                        : "transparent",
                      borderBottom: "1px solid var(--neutral-100)",
                      boxShadow: "none",
                      "&:before": {
                        display: "none",
                      },
                      ...(client.portfolios?.length === 0 && {
                        pointerEvents: "none",
                        opacity: 0.8,
                        cursor: "not-allowed",
                      }),
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AccordionSummary
                      expandIcon={<ArrowDownIcon width={20} />}
                      sx={{
                        padding: "4px 8px",
                        minHeight: "32px",
                        "&.Mui-expanded": {
                          minHeight: "32px",
                        },
                        "& .MuiAccordionSummary-content": {
                          margin: 0,
                          padding: 0,
                          minHeight: "32px",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <GKCheckbox
                        onClick={(e) => e.stopPropagation()}
                        disabled={client.portfolios.length === 0}
                        sx={{ p: "0 !important" }}
                        checked={
                          client.portfolios &&
                          client.portfolios.length > 0 &&
                          client.portfolios.every((p: any) =>
                            selectedItems.includes(p.id || p.portfolioId),
                          )
                        }
                        indeterminate={
                          client.portfolios &&
                          client.portfolios.length > 0 &&
                          client.portfolios.some((p: any) =>
                            selectedItems.includes(p.id || p.portfolioId),
                          ) &&
                          !client.portfolios.every((p: any) =>
                            selectedItems.includes(p.id || p.portfolioId),
                          )
                        }
                        onChange={(e) => {
                          if (client.portfolios.length === 0) return;

                          e.stopPropagation();
                          const isChecked = e.target.checked;

                          const portfolioIds = client.portfolios.map(
                            (p: any) => p.id || p.portfolioId,
                          );

                          setSelectedItems((prev) => {
                            if (isChecked) {
                              return Array.from(
                                new Set([...prev, ...portfolioIds]),
                              );
                            }
                            return prev.filter(
                              (id) => !portfolioIds.includes(id),
                            );
                          });
                        }}
                      />

                      <Typography fontSize={12} fontWeight={500} color="#333">
                        {client.name} - {client.code}
                      </Typography>
                      {client.portfolios?.length === 0 ? (
                        <Box
                          sx={{
                            marginLeft: "auto",
                            mr: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <InfoItalicIcon color="var(--grey-500)" />

                          <Typography
                            fontSize={10}
                            fontWeight={400}
                            color="var(--grey-700)"
                            fontStyle="italic"
                          >
                            No portfolios available for this client
                          </Typography>
                        </Box>
                      ) : (
                        client.portfolios?.some((p: any) =>
                          selectedItems.includes(p.id || p.portfolioId),
                        ) && (
                          <Box sx={{ marginLeft: "auto", mr: 1 }}>
                            <Typography
                              fontSize={10}
                              fontWeight={400}
                              color="var(--grey-500)"
                            >
                              {`${
                                client.portfolios?.filter((p: any) =>
                                  selectedItems.includes(p.id || p.portfolioId),
                                ).length
                              }/${client.portfolios?.length} Portfolios Selected`}
                            </Typography>
                          </Box>
                        )
                      )}
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        paddingLeft: "40px",
                        paddingTop: 0,
                        paddingBottom: 1,
                        mt: 0.5,
                      }}
                    >
                      {client.portfolios?.map((i: any) => (
                        <Box
                          key={i.id}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{ mb: 1 }}
                        >
                          <GKCheckbox
                            disabled={client.portfolios?.length === 0}
                            checked={selectedItems.includes(
                              i.id || i.portfolioId,
                            )}
                            onChange={(e) => {
                              if (client.portfolios?.length === 0) return;
                              e.stopPropagation();

                              const isChecked = e.target.checked;
                              const portfolioId = i.id || i.portfolioId;

                              setSelectedItems((prev) => {
                                if (isChecked) return [...prev, portfolioId];
                                return prev.filter((p) => p !== portfolioId);
                              });
                            }}
                            sx={{ p: "0 !important", transform: "scale(0.8)" }}
                          />

                          <Typography
                            fontSize={12}
                            fontWeight={400}
                            color="#333"
                          >
                            {i.name} - {i.portfoioId}
                          </Typography>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Card>
            )}
          </Grid2>
        </Grid2>
      </GKModal>

      <SelectFamilyModal
        open={openFamilyModal}
        setOpen={setIsOpenFamilyModal}
        portfolioList={portfolioList}
        onFamilySelect={(id: number) => {
          const fam = portfolioList?.find((f) => f.id === id);
          setSelectedFamilyId(id);
          setSelectedFamilyName(fam?.name ?? null);
          setSearchText("");
        }}
      />
    </>
  );
};

export default FilterModal;
